/**
 * Utility functions for handling fetch API errors
 */

export interface FetchError extends Error {
  status?: number;
  statusText?: string;
  body?: any;
  code?: string;
}

/**
 * Creates a structured error from a fetch response
 */
export async function createFetchError(response: Response): Promise<FetchError> {
  let errorBody: any;
  let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

  // Try to parse the response body
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      errorBody = await response.json();
      // Extract message from common error response formats
      if (errorBody.message) {
        // Check if the message is a stringified JSON (backend double encoding issue)
        if (typeof errorBody.message === 'string' && errorBody.message.startsWith('{')) {
          try {
            const parsedMessage = JSON.parse(errorBody.message);
            errorMessage = parsedMessage.error || parsedMessage.message || errorBody.message;
            errorBody = parsedMessage; // Use the parsed object as the body
          } catch {
            errorMessage = errorBody.message;
          }
        } else {
          errorMessage = errorBody.message;
        }
      } else if (errorBody.error && typeof errorBody.error === 'string') {
        errorMessage = errorBody.error;
      } else if (errorBody.error?.message) {
        errorMessage = errorBody.error.message;
      }
    } else {
      // Try to get text for non-JSON responses
      const text = await response.text();
      if (text) {
        // Check if text is JSON
        if (text.startsWith('{')) {
          try {
            const parsed = JSON.parse(text);
            errorMessage = parsed.error || parsed.message || text;
            errorBody = parsed;
          } catch {
            errorMessage = text;
            errorBody = { message: text };
          }
        } else {
          errorMessage = text;
          errorBody = { message: text };
        }
      }
    }
  } catch (e) {
    // If we can't parse the body, use the default message
    console.error('Failed to parse error response:', e);
  }

  const error = new Error(errorMessage) as FetchError;
  error.status = response.status;
  error.statusText = response.statusText;
  error.body = errorBody;

  // Add specific error codes for common scenarios
  if (response.status === 401) {
    error.code = 'UNAUTHORIZED';
  } else if (response.status === 429) {
    error.code = 'RATE_LIMIT';
  } else if (response.status === 0) {
    error.code = 'NETWORK_ERROR';
    error.message = 'Network error: Unable to connect to the server';
  } else if (response.status >= 500) {
    error.code = 'SERVER_ERROR';
  }

  return error;
}

/**
 * Formats an error for user display
 */
export function formatErrorForUser(error: FetchError | Error): string {
  if ('status' in error && error.status) {
    switch (error.status) {
      case 401:
        return 'Authentication failed. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Rate limit exceeded. Please wait before trying again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'Server error. Please try again later.';
      default:
        return error.message || 'An unexpected error occurred';
    }
  }

  // Handle network errors
  if ('code' in error && error.code === 'NETWORK_ERROR') {
    return 'Network error: Please check your internet connection';
  }

  return error.message || 'An unexpected error occurred';
}

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error: FetchError | Error): boolean {
  if ('status' in error && error.status) {
    // Retry on server errors and rate limits
    return error.status >= 500 || error.status === 429;
  }

  // Retry on network errors
  if ('code' in error && error.code === 'NETWORK_ERROR') {
    return true;
  }

  return false;
}

/**
 * Extracts detailed error information for logging
 */
export function extractErrorDetails(error: FetchError | Error): Record<string, any> {
  const details: Record<string, any> = {
    message: error.message,
    name: error.name,
    stack: error.stack,
  };

  if ('status' in error) {
    details["status"] = error.status;
    details["statusText"] = error.statusText;
    details["body"] = error.body;
    details["code"] = error.code;
  }

  return details;
}
